import { useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import ExerciseLineRow from "./ExerciseLineRow";
import type { WorkoutGeneratorBlock } from "@/integrations/backend/workout-generator";

type Props = {
  block: WorkoutGeneratorBlock;
  busyLineId: string | null;
  busyBlock: boolean;
  parsing: boolean;
  onInclude: (lineId: string) => Promise<void>;
  onExclude: (lineId: string) => Promise<void>;
  onUndoInclude: (lineId: string) => Promise<void>;
  onAddExercise: (lineId: string, exerciseId: string) => Promise<void>;
  onRemoveExercise: (lineId: string, mappingId: string) => Promise<void>;
  onFinalize: (lineId: string) => Promise<void>;
  onReopen: (lineId: string) => Promise<void>;
  onAiGenerate: (lineId: string) => Promise<void>;
  onAddPool: (blockId: string, label?: string) => Promise<void>;
};

function AddExercisePoolControl({
  busy,
  onAdd,
}: {
  busy: boolean;
  onAdd: (label?: string) => Promise<void>;
}) {
  const [expanded, setExpanded] = useState(false);
  const [label, setLabel] = useState("");
  const [adding, setAdding] = useState(false);

  if (!expanded) {
    return (
      <button
        type="button"
        className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 bg-white px-3 py-2.5 text-sm font-medium text-gray-800 shadow-sm transition-colors hover:border-gray-400 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
        disabled={busy || adding}
        onClick={() => setExpanded(true)}
      >
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 text-gray-700">
          <Plus className="h-3.5 w-3.5" aria-hidden />
        </span>
        Add an exercise pool
      </button>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 shadow-sm">
      <p className="mb-2 text-sm font-medium text-gray-900">New exercise pool</p>
      <div className="space-y-3">
        <Input
          placeholder="Pool label (optional)"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          className="h-9 border-gray-300 bg-white text-sm text-gray-900 placeholder:text-gray-500 focus-visible:ring-gray-400"
          disabled={busy || adding}
        />
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            className="bg-gray-900 text-white hover:bg-gray-800"
            disabled={busy || adding}
            onClick={() => {
              setAdding(true);
              void onAdd(label.trim() || undefined)
                .then(() => {
                  setExpanded(false);
                  setLabel("");
                })
                .finally(() => setAdding(false));
            }}
          >
            {adding ? <Loader2 className="h-3 w-3 animate-spin" /> : "Add pool"}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="text-gray-700 hover:bg-gray-200 hover:text-gray-900"
            disabled={busy || adding}
            onClick={() => {
              setExpanded(false);
              setLabel("");
            }}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}

function ExercisePoolsColumn({
  block,
  busyLineId,
  busyBlock,
  parsing,
  onInclude,
  onExclude,
  onUndoInclude,
  onAddExercise,
  onRemoveExercise,
  onFinalize,
  onReopen,
  onAiGenerate,
  onAddPool,
}: Omit<Props, "block"> & { block: WorkoutGeneratorBlock }) {
  const showLoading =
    parsing || block.parse_status === "pending" || block.parse_status === "loading";
  const canAddPool = !showLoading;

  if (showLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading…
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {block.parse_status === "error" ? (
        <p className="text-sm text-red-600">{block.parse_error || "Parse failed"}</p>
      ) : null}
      {block.parse_status === "skipped" && block.lines.length === 0 ? (
        <p className="text-xs text-gray-500">No exercises in this block</p>
      ) : null}
      {block.parse_status === "complete" && block.lines.length === 0 ? (
        <p className="text-xs text-gray-500">No exercises parsed</p>
      ) : null}
      {block.lines.map((line) => (
        <ExerciseLineRow
          key={line.id}
          line={line}
          busy={busyLineId === line.id}
          onInclude={() => onInclude(line.id)}
          onExclude={() => onExclude(line.id)}
          onUndoInclude={() => onUndoInclude(line.id)}
          onAddExercise={(exerciseId) => onAddExercise(line.id, exerciseId)}
          onRemoveExercise={(mappingId) => onRemoveExercise(line.id, mappingId)}
          onFinalize={() => onFinalize(line.id)}
          onReopen={() => onReopen(line.id)}
          onAiGenerate={() => onAiGenerate(line.id)}
        />
      ))}
      {canAddPool ? (
        <AddExercisePoolControl
          busy={busyBlock || busyLineId !== null}
          onAdd={(label) => onAddPool(block.id, label)}
        />
      ) : null}
    </div>
  );
}

export default function SessionBlockRow({
  block,
  busyLineId,
  busyBlock,
  parsing,
  onInclude,
  onExclude,
  onUndoInclude,
  onAddExercise,
  onRemoveExercise,
  onFinalize,
  onReopen,
  onAiGenerate,
  onAddPool,
}: Props) {
  return (
    <tr className="border-b border-gray-100 align-top">
      <td className="px-3 py-3 font-medium text-gray-900">{block.session_block_text || "—"}</td>
      <td className="px-3 py-3 text-gray-700 whitespace-pre-wrap">{block.series_cell_text || "—"}</td>
      <td className="px-3 py-3">
        <ExercisePoolsColumn
          block={block}
          busyLineId={busyLineId}
          busyBlock={busyBlock}
          parsing={parsing}
          onInclude={onInclude}
          onExclude={onExclude}
          onUndoInclude={onUndoInclude}
          onAddExercise={onAddExercise}
          onRemoveExercise={onRemoveExercise}
          onFinalize={onFinalize}
          onReopen={onReopen}
          onAiGenerate={onAiGenerate}
          onAddPool={onAddPool}
        />
      </td>
    </tr>
  );
}
